import gql from "graphql-tag";

export const CREATE_TASK = gql`
  mutation CreateTask(
    $assigneeId: ID!
    $description: String
    $dueAt: DateTime
    $organizationId: ID!
    $incidentId: ID
    $priority: String!
    $resolution: String!
    $taskTypeId: ID!
    $title: String!
    $file: Upload
  ) {
    createTask(
      assigneeId: $assigneeId
      incidentId: $incidentId
      description: $description
      dueAt: $dueAt
      organizationId: $organizationId
      priority: $priority
      resolution: $resolution
      taskTypeId: $taskTypeId
      title: $title
      file: $file
    ) {
      id
      description
      assignee {
        id
        firstName
        lastName
        roles {
          id
          role {
            id
            name
          }
        }
      }
      files {
        file
        fileUrl
      }
      dueAt
      priority
      organization {
        id
        name
        facility: ancestors(type: "facility") {
          id
          name
        }
        organization: ancestors(type: "organization") {
          id
          name
        }
      }
      title
      taskType {
        id
        name
      }
      taskId
      resolution
    }
  }
`;

export const TASK_TYPES = gql`
  query TaskTypes($sort: String, $status: String) {
    taskTypes(sort: $sort, status: $status) {
      id
      name
      status
    }
  }
`;

export const UNIT_WITH_PARENTS = gql`
  query unitWithParents($id: ID!, $types: [String!], $searchText: String) {
    organizationDescendants(id: $id, types: $types, searchText: $searchText) {
      name
      type
      id
      facility: ancestors(type: "facility") {
        id
        name
        code
      }
      ancestors(types: ["facility", "organization", "region"]) {
        id
        name
      }
    }
  }
`;

export const TASKS = gql`
  query Tasks(
    $assigneeId: ID
    $dateTimeFilter: DateTimeFilter
    $parentId: ID
    $parentIds: [ID]
    $priorities: [String]
    $resolutions: [String]
    $taskTypes: [ID]
    $isOverdue: Boolean
    $organizationType: String
    $organizationSubtypes: [String]
  ) {
    tasks(
      assigneeId: $assigneeId
      dateTimeFilter: $dateTimeFilter
      parentId: $parentId
      parentIds: $parentIds
      priorities: $priorities
      resolutions: $resolutions
      taskTypes: $taskTypes
      isOverdue: $isOverdue
      organizationType: $organizationType
      organizationSubtypes: $organizationSubtypes
      sortByPriorityWeight: "desc"
    ) {
      id
      actions {
        checklist {
          id
          completed
          description
          required
          comment
        }
        photoVideoRequired
        preInstallMethod
        targetCount
        unitTypeRanking
        vacantByType
        vacantCount
        assignments
        assignmentsByType
      }
      description
      assignee {
        id
        firstName
        lastName
        roles {
          id
          role {
            id
            name
          }
        }
      }
      dueAt
      overdue
      priority
      organization {
        id
        name
        type
        subtype
        timeZone
        facility: ancestors(type: "facility") {
          id
          name
        }
        organization: ancestors(type: "organization") {
          id
          name
        }
      }
      title
      taskType {
        id
        name
      }
      taskId
      resolution
      taskRule {
        conditions {
          method
          vacancyThreshold
          targetBelowThreshold
          targetAtOrAboveThreshold
          fixedTargetUnits
          devicesPerUnit
        }
      }
    }
  }
`;

export const TASKS_PAGINATED = gql`
  query TasksPaginated(
    $assigneeId: ID
    $dateTimeFilter: DateTimeFilter
    $parentId: ID
    $parentIds: [ID]
    $priorities: [String]
    $resolutions: [String]
    $taskTypes: [ID]
    $isOverdue: Boolean
    $organizationType: String
    $organizationSubtypes: [String]
    $limit: Int
    $page: Int
  ) {
    tasksPaginated(
      assigneeId: $assigneeId
      dateTimeFilter: $dateTimeFilter
      parentId: $parentId
      parentIds: $parentIds
      priorities: $priorities
      resolutions: $resolutions
      taskTypes: $taskTypes
      isOverdue: $isOverdue
      organizationType: $organizationType
      organizationSubtypes: $organizationSubtypes
      sortByPriorityWeight: "desc"
      limit: $limit
      page: $page
    ) {
      data {
        id
        actions {
          checklist {
            id
            completed
            description
            required
            comment
          }
          photoVideoRequired
          preInstallMethod
          targetCount
          unitTypeRanking
          vacantByType
          vacantCount
          assignments
          assignmentsByType
        }
        description
        assignee {
          id
          firstName
          lastName
          roles {
            id
            role {
              id
              name
            }
          }
        }
        dueAt
        overdue
        priority
        organization {
          id
          name
          type
          subtype
          facility: ancestors(type: "facility") {
            id
            name
          }
          organization: ancestors(type: "organization") {
            id
            name
          }
        }
        title
        taskType {
          id
          name
        }
        taskId
        resolution
        taskRule {
          conditions {
            method
            vacancyThreshold
            targetBelowThreshold
            targetAtOrAboveThreshold
            fixedTargetUnits
            devicesPerUnit
          }
        }
      }
      page {
        totalCount
        totalPages
        currentPage
        pageSize
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const GET_PREINSTALL_TASK = gql`
  query GetPreInstallTask($id: ID!) {
    findTask(id: $id) {
      id
      description
      actions {
        checklist {
          id
          completed
          description
          required
          comment
        }
        photoVideoRequired
        preInstallMethod
        targetCount
        unitTypeRanking
        vacantByType
        vacantCount
        assignments
        assignmentsByType
      }
      assignee {
        id
        firstName
        lastName
        roles {
          id
          role {
            id
            name
          }
        }
      }
      dueAt
      priority
      organization {
        id
        name
        type
        subtype
        timeZone
        facility: ancestors(type: "facility") {
          id
          name
        }
        organization: ancestors(type: "organization") {
          id
          name
        }
      }
      title
      taskType {
        id
        name
      }
      taskId
      resolution
      taskRule {
        conditions {
          method
          vacancyThreshold
          targetBelowThreshold
          targetAtOrAboveThreshold
          fixedTargetUnits
          devicesPerUnit
        }
      }
    }
  }
`;

export const GET_TASK = gql`
  query FindTask($id: ID!) {
    findTask(id: $id) {
      id
      description
      actions {
        checklist {
          id
          completed
          description
          required
          comment
        }
        photoVideoRequired
        preInstallMethod
        targetCount
        unitTypeRanking
        vacantByType
        vacantCount
        assignments
        assignmentsByType
      }
      taskRule {
        id
        name
        conditions {
          method
          devicesPerUnit
          fixedTargetUnits
          vacancyThreshold
          targetBelowThreshold
          targetAtOrAboveThreshold
        }
      }
      assignee {
        id
        firstName
        lastName
        roles {
          id
          role {
            id
            name
          }
        }
      }
      files {
        id
        file
        fileUrl
      }
      dueAt
      priority
      organization {
        id
        name
      }
      title
      taskType {
        id
        name
      }
      taskId
      resolution
    }
  }
`;

export const UPDATE_TASK = gql`
  mutation UpdateTask(
    $id: ID!
    $assigneeId: ID!
    $description: String
    $dueAt: DateTime
    $organizationId: ID!
    $priority: String!
    $resolution: String!
    $taskTypeId: ID!
    $title: String!
    $actions: InputTaskActions
  ) {
    updateTask(
      id: $id
      assigneeId: $assigneeId
      description: $description
      dueAt: $dueAt
      organizationId: $organizationId
      priority: $priority
      resolution: $resolution
      taskTypeId: $taskTypeId
      title: $title
      actions: $actions
    ) {
      id
      actions {
        checklist {
          id
          completed
          description
          required
          comment
        }
        assignments
        assignmentsByType
      }
      description
      dueAt
      priority
      resolution
      title
    }
  }
`;

export const UPDATE_TASK_TYPE = gql`
  mutation UpdateTaskType($id: ID!, $name: String, $status: String) {
    updateTaskType(id: $id, name: $name, status: $status) {
      id
      name
      status
    }
  }
`;

export const TASK_HISTORY = gql`
  query TaskHistory($taskId: ID!) {
    taskHistory(taskId: $taskId) {
      id
      entity
      entityId
      operation
      changeset
      newData
      insertedAt
      updatedAt
      user {
        id
        firstName
        lastName
        roles {
          role {
            id
            name
          }
        }
      }
    }
  }
`;

export const CREATE_TASK_COMMENT = gql`
  mutation CreateTaskComment($taskId: ID!, $content: String!) {
    createTaskComment(taskId: $taskId, content: $content) {
      id
      content
    }
  }
`;

export const TASK_COMMENTS = gql`
  query Comments($taskId: ID!) {
    taskComments(taskId: $taskId) {
      id
      content
      creator {
        firstName
        lastName
        roles {
          role {
            id
            name
          }
        }
      }
      insertedAt
    }
  }
`;

export const DELETE_TASK_COMMENT = gql`
  mutation DeleteTaskComment($id: ID!) {
    deleteTaskComment(id: $id) {
      id
    }
  }
`;

export const ADD_TASK_ATTACHMENT = gql`
  mutation AddTaskAttachment($taskId: ID!, $file: Upload!) {
    addTaskAttachment(taskId: $taskId, file: $file) {
      id
      file
      fileUrl
    }
  }
`;

export const DELETE_TASK_ATTACHMENT = gql`
  mutation DeleteTaskAttachment($id: ID!) {
    deleteTaskAttachment(id: $id) {
      id
    }
  }
`;

export const GET_TASK_INCIDENTS = gql`
  query FindTask($id: ID!, $incidentId: ID!) {
    findTask(id: $id) {
      id
      description
      organization {
        ancestors(type: "facility") {
          id
          name
        }
      }
      assignee {
        id
        firstName
        lastName
        roles {
          id
          role {
            id
            name
          }
        }
      }
      files {
        id
        file
        fileUrl
      }
      dueAt
      priority
      organization {
        id
        name
        devices {
          externalId
          type
        }
      }
      title
      taskType {
        id
        name
      }
      taskId
      resolution
    }
    incident(id: $incidentId) {
      startedAt
      closedAt
      type
    }
  }
`;

export const GET_DISABLED_LIEN_UNITS = gql`
  query GetDisabledLienUnits($facilityId: ID!) {
    organizationDescendants(
      id: $facilityId
      accountStatus: "disabled_lien"
      type: "zone"
      subtype: "unit"
    ) {
      id
      name
      type
      subtype
      billingAccounts {
        id
        externalId
        status
        name
        type
        users {
          firstName
          lastName
        }
      }
    }
  }
`;
export const TODAYS_TASKS_COUNT = gql`
  query TodaysTasksCount(
    $parentId: ID!
    $dateTimeFilter: DateTimeFilter!
    $taskTypes: [ID]
  ) {
    todaysTasksCount: tasksPaginated(
      parentId: $parentId
      dateTimeFilter: $dateTimeFilter
      resolutions: ["todo", "in_progress", "in_review", "pending"]
      taskTypes: $taskTypes
      limit: 1
      page: 1
    ) {
      page {
        totalCount
      }
    }
  }
`;

export const OVERDUE_TASKS_COUNT = gql`
  query OverdueTasksCount(
    $parentId: ID!
    $taskTypes: [ID]
    $dateTimeFilter: DateTimeFilter!
  ) {
    overdueTasksCount: tasksPaginated(
      parentId: $parentId
      isOverdue: true
      resolutions: ["todo", "in_progress", "in_review", "pending"]
      dateTimeFilter: $dateTimeFilter
      taskTypes: $taskTypes
      limit: 1
      page: 1
    ) {
      page {
        totalCount
      }
    }
  }
`;

export const DASHBOARD_OVERDUE_TASKS = gql`
  query DashboardOverdueTasks(
    $parentId: ID!
    $taskTypes: [ID]
    $dateTimeFilter: DateTimeFilter!
    $limit: Int
    $resolutions: [String]
    $priorities: [String]
    $isOverdue: Boolean
  ) {
    tasksPaginated(
      parentId: $parentId
      isOverdue: $isOverdue
      resolutions: $resolutions
      priorities: $priorities
      dateTimeFilter: $dateTimeFilter
      taskTypes: $taskTypes
      sortByPriorityWeight: "desc"
      limit: $limit
      page: 1
    ) {
      data {
        id
        actions {
          checklist {
            id
            completed
            description
            required
            comment
          }
          photoVideoRequired
          preInstallMethod
          targetCount
          unitTypeRanking
          vacantByType
          vacantCount
          assignments
          assignmentsByType
        }
        description
        assignee {
          id
          firstName
          lastName
          roles {
            id
            role {
              id
              name
            }
          }
        }
        dueAt
        overdue
        priority
        organization {
          id
          name
          type
          subtype
          timeZone
          facility: ancestors(type: "facility") {
            id
            name
          }
          organization: ancestors(type: "organization") {
            id
            name
          }
        }
        title
        taskType {
          id
          name
        }
        taskId
        resolution
        taskRule {
          conditions {
            method
            vacancyThreshold
            targetBelowThreshold
            targetAtOrAboveThreshold
            fixedTargetUnits
            devicesPerUnit
          }
        }
      }
      page {
        totalCount
      }
    }
  }
`;

export const DASHBOARD_FUTURE_TASKS = gql`
  query DashboardFutureTasks(
    $parentId: ID!
    $dateTimeFilter: DateTimeFilter!
    $priorities: [String]
    $taskTypes: [ID]
    $resolutions: [String]
    $limit: Int
  ) {
    tasksPaginated(
      parentId: $parentId
      dateTimeFilter: $dateTimeFilter
      resolutions: $resolutions
      priorities: $priorities
      taskTypes: $taskTypes
      sortByPriorityWeight: "desc"
      limit: $limit
      page: 1
    ) {
      data {
        id
        actions {
          checklist {
            id
            completed
            description
            required
            comment
          }
          photoVideoRequired
          preInstallMethod
          targetCount
          unitTypeRanking
          vacantByType
          vacantCount
          assignments
          assignmentsByType
        }
        description
        assignee {
          id
          firstName
          lastName
          roles {
            id
            role {
              id
              name
            }
          }
        }
        dueAt
        overdue
        priority
        organization {
          id
          name
          type
          subtype
          timeZone
          facility: ancestors(type: "facility") {
            id
            name
          }
          organization: ancestors(type: "organization") {
            id
            name
          }
        }
        title
        taskType {
          id
          name
        }
        taskId
        resolution
        taskRule {
          conditions {
            method
            vacancyThreshold
            targetBelowThreshold
            targetAtOrAboveThreshold
            fixedTargetUnits
            devicesPerUnit
          }
        }
      }
      page {
        totalCount
      }
    }
  }
`;
